import Component from "vue-class-component";
import { Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";

const defaultActivityUpdateThrottleMs: number = 1000;
const SiteMonitor = namespace("siteMonitor");

@Component
export default class SiteMonitorActivityTrackerMixin extends Vue {
	@SiteMonitor.Mutation public setActivity: () => void;

	private activityMousedownHandler: ((e: MouseEvent) => void) | null = null;
	private activityKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
	private activityDragstartHandler: ((e: DragEvent) => void) | null = null;
	private activityWheelHandler: ((e: WheelEvent) => void) | null = null;
	private lastActivityTimestamp: number = 0;
	private rootElement: HTMLElement | null = null;

	public registerActivityListeners(rootHtmlElement: HTMLElement | null = null): void {

		this.rootElement = rootHtmlElement ?? this.$el as HTMLElement;

		this.unregisterActivityListeners();

		this.activityMousedownHandler = (_e: MouseEvent) => {
			this.markActivity();
		};
		this.activityKeydownHandler = (e: KeyboardEvent) => {
			const isTabKey = e.key === "Tab";
			const canScrollWithArrowKey = this.canScrollWithArrowKey(this.rootElement as HTMLElement, e);
			if ((!isTabKey && !canScrollWithArrowKey && !this.isTypingElement(e.target)) || e.repeat || this.isModifierOnlyKey(e.key)) {
				return;
			}
			this.markActivity();
		};
		this.activityDragstartHandler = (_e: DragEvent) => {
			this.markActivity();
		};
		this.activityWheelHandler = (e: WheelEvent) => {
			if (!this.canScrollWithWheel(this.rootElement as HTMLElement, e)) {
				return;
			}
			this.markActivity();
		};

		this.rootElement.addEventListener("mousedown", this.activityMousedownHandler);
		this.rootElement.addEventListener("keydown", this.activityKeydownHandler);
		this.rootElement.addEventListener("dragstart", this.activityDragstartHandler);
		this.rootElement.addEventListener("wheel", this.activityWheelHandler, { passive: true });
	}

	public unregisterActivityListeners(): void {
		if (!this.rootElement) {
			return;
		}

		if (this.activityMousedownHandler) {
			this.rootElement.removeEventListener("mousedown", this.activityMousedownHandler);
		}
		if (this.activityKeydownHandler) {
			this.rootElement.removeEventListener("keydown", this.activityKeydownHandler);
		}
		if (this.activityDragstartHandler) {
			this.rootElement.removeEventListener("dragstart", this.activityDragstartHandler);
		}
		if (this.activityWheelHandler) {
			this.rootElement.removeEventListener("wheel", this.activityWheelHandler);
		}

		this.activityMousedownHandler = null;
		this.activityKeydownHandler = null;
		this.activityDragstartHandler = null;
		this.activityWheelHandler = null;
	}

	private markActivity(): void {
		const now = Date.now();
		if (now - this.lastActivityTimestamp < defaultActivityUpdateThrottleMs) {
			return;
		}

		this.lastActivityTimestamp = now;
		this.setActivity();
	}

	private isModifierOnlyKey(key: string): boolean {
		return key === "Shift" || key === "Control" || key === "Alt" || key === "Meta";
	}

	private isTypingElement(target: EventTarget | null): boolean {
		if (!(target instanceof HTMLElement)) {
			return false;
		}

		if (target.isContentEditable || target instanceof HTMLTextAreaElement) {
			return true;
		}

		if (target instanceof HTMLInputElement) {
			const nonTypingInputTypes = ["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"];
			return !nonTypingInputTypes.includes(target.type);
		}

		return false;
	}

	private canScrollWithArrowKey(activityRoot: HTMLElement, event: KeyboardEvent): boolean {
		if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
			return false;
		}

		return this.canScrollInDirection(activityRoot, event.target, event.key === "ArrowUp" ? "up" : "down");
	}

	private canScrollWithWheel(activityRoot: HTMLElement, event: WheelEvent): boolean {
		if (event.deltaY === 0) {
			return false;
		}

		return this.canScrollInDirection(activityRoot, event.target, event.deltaY < 0 ? "up" : "down");
	}

	private canScrollInDirection(activityRoot: HTMLElement, target: EventTarget | null, direction: "up" | "down"): boolean {
		const scrollableAncestor = this.findScrollableAncestor(activityRoot, target);
		if (!scrollableAncestor) {
			return false;
		}

		if (direction === "up") {
			return scrollableAncestor.scrollTop > 0;
		}

		return scrollableAncestor.scrollTop + scrollableAncestor.clientHeight < scrollableAncestor.scrollHeight;
	}

	private findScrollableAncestor(activityRoot: HTMLElement, target: EventTarget | null): HTMLElement | null {
		if (!(target instanceof HTMLElement)) {
			return null;
		}

		let current: HTMLElement | null = target;
		while (current) {
			if (this.isScrollableElement(current)) {
				return current;
			}

			if (current === activityRoot) {
				break;
			}

			current = current.parentElement;
		}

		return null;
	}

	private isScrollableElement(element: HTMLElement): boolean {
		const style = window.getComputedStyle(element);
		const allowsVerticalScroll = style.overflowY === "auto" || style.overflowY === "scroll";
		return allowsVerticalScroll && element.scrollHeight > element.clientHeight;
	}
}